import { Box, Chip, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Banner, Typography } from "@foodpilot/foods";
import { visuallyHidden } from "@mui/utils";
import { CompanyAutocomplete, type CompanyOption } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { RemovableTag } from "src/components/AssociatedStructures";
import { FormHelperBox } from "src/components/EntityForm/FormHelperBox";

type AssociatedOrganizationsFieldProps = {
    appearance?: "legacy" | "product-v2";
    value: CompanyOption[];
    onChange: (organizations: CompanyOption[]) => void;
    sharesElements?: boolean;
};

export const AssociatedOrganizationsField: FC<AssociatedOrganizationsFieldProps> = ({
    appearance = "legacy",
    value,
    onChange,
    sharesElements = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { selectedCompany } = useFoodpilotContext();
    const generatedId = useId();
    const labelId = appearance === "product-v2" ? `packaging-organizations-label-${generatedId}` : undefined;
    const organizations = useMemo(() => value ?? [], [value]);
    const excludeIds = useMemo(
        () =>
            [selectedCompany?.id, ...organizations.map((organization) => organization.id)].filter(
                (id): id is number => id !== undefined,
            ),
        [selectedCompany?.id, organizations],
    );

    const helpText =
        sharesElements ?
            t(
                "packaging_composite.associated_organizations_help",
                "Chosen organizations will have access to this packaging and to all its elements.",
            )
        :   t(
                "packaging_element.associated_organizations_help",
                "Chosen organizations will have access to this packaging.",
            );

    return (
        <Stack gap={2}>
            {appearance === "product-v2" ?
                <Typography id={labelId} variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                    {t("packaging_element.associated_organizations", "Associated organizations")}
                </Typography>
            :   <MuiTypography variant="body-semibold">
                    {t("packaging_element.associated_organizations", "Associated organizations")}
                </MuiTypography>
            }
            {appearance === "product-v2" && organizations.length === 0 && (
                <Typography component="span" role="status" sx={visuallyHidden}>
                    {t("packaging_element.no_associated_organizations", "No organizations selected")}
                </Typography>
            )}
            {appearance === "product-v2" && organizations.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {organizations.map((organization) => (
                        <RemovableTag
                            key={organization.id}
                            label={organization.name}
                            backgroundColor={theme.custom.grey[400]}
                            foregroundColor={theme.custom.grey[2500]}
                            removeLabel={t("product.v2.organization.remove_associated_entity", "Remove {{name}}", {
                                name: organization.name,
                            })}
                            onRemove={() => onChange(organizations.filter(({ id }) => id !== organization.id))}
                        />
                    ))}
                </Box>
            )}
            {appearance === "legacy" && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {organizations.length === 0 ?
                        <Chip
                            label={t("packaging_element.no_associated_organizations", "No organizations selected")}
                            variant="outlined"
                            sx={{ color: "text.secondary" }}
                        />
                    :   organizations.map((organization) => (
                            <Chip
                                key={organization.id}
                                label={organization.name}
                                onDelete={() => onChange(organizations.filter(({ id }) => id !== organization.id))}
                            />
                        ))
                    }
                </Box>
            )}
            <CompanyAutocomplete
                value={[]}
                onChange={(nextOrganizations) => onChange([...organizations, ...nextOrganizations])}
                placeholder={t("packaging_element.choose_organizations_placeholder", "Choose organizations")}
                appearance={appearance}
                labelId={labelId}
                excludeIds={excludeIds}
                hierarchyRootId={selectedCompany?.id}
            />
            {appearance === "product-v2" ?
                <FormHelperBox>{helpText}</FormHelperBox>
            :   <Banner variant="themed">
                    <MuiTypography variant="body-regular">{helpText}</MuiTypography>
                </Banner>
            }
        </Stack>
    );
};
